import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { UserCheck, Mail, Phone, MapPin, Award, Clock, MessageSquare, Search, Filter, Heart, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateConsultantDialog } from '@/components/forms/CreateConsultantDialog';
import { useDbFavorites } from '@/hooks/useDbFavorites';
import { useFavorites } from '@/hooks/useFavorites';
import { cn } from '@/lib/utils';

interface Consultant {
  id: string;
  name: string;
  specialty: string;
  expertise: string[] | null;
  location: string | null;
  email: string | null;
  phone: string | null;
  hourly_rate: number | null;
  currency: string;
  rating: number;
  projects_completed: number;
  availability: string;
}

const specialties = ['All', 'M&A Advisory', 'Business Valuation', 'Due Diligence', 'Tax Advisory', 'Legal', 'Financial Planning', 'Other'];

const Consultants = () => {
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('All');
  const [availabilityFilter, setAvailabilityFilter] = useState('All');
  const { toast } = useToast();
  const { isFavorite, toggleFavorite } = useDbFavorites('consultant');

  const fetchConsultants = async () => {
    try {const snap = await getDocs(query(collection(db, 'consultants'), orderBy('rating', 'desc')));
setConsultants(snap.docs.map(d => {
const data = d.data();
return {
id: d.id,
name: data.name || '',
specialty: data.specialty || '',
expertise: data.expertise || null,
location: data.location || null,
email: data.email || null,
phone: data.phone || null,
hourly_rate: data.hourlyRate || data.hourly_rate || null,
currency: data.currency || 'CAD',
rating: data.rating || 0,
projects_completed: data.projectsCompleted || data.projects_completed || 0,
availability: data.availability || 'available',
} as Consultant;
}));

    } catch (error) {
      console.error('Error fetching consultants:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les consultants',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultants();
  }, []);

  const filteredConsultants = consultants.filter((consultant) => {
    const matchesSearch = 
      consultant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      consultant.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      consultant.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      consultant.expertise?.some(e => e.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSpecialty = specialtyFilter === 'All' || consultant.specialty === specialtyFilter;
    const matchesAvailability = availabilityFilter === 'All' || consultant.availability === availabilityFilter;

    return matchesSearch && matchesSpecialty && matchesAvailability;
  });

  const getAvailabilityBadge = (availability: string) => {
    switch (availability) {
      case 'available':
        return <Badge variant="default">Disponible</Badge>;
      case 'limited':
        return <Badge variant="secondary">Limité</Badge>;
      case 'unavailable':
        return <Badge variant="destructive">Indisponible</Badge>;
      default:
        return <Badge variant="outline">{availability}</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Skeleton className="h-8 w-48 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-12 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Consultants</h1>
            <p className="text-muted-foreground">Experts consultants pour vos transactions</p>
          </div>
          <CreateConsultantDialog onSuccess={fetchConsultants} />
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, spécialité, expertise..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Spécialité" />
              </SelectTrigger>
              <SelectContent>
                {specialties.map((specialty) => (
                  <SelectItem key={specialty} value={specialty}>
                    {specialty === 'All' ? 'Toutes spécialités' : specialty}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Clock className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Disponibilité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">Toutes disponibilités</SelectItem>
                <SelectItem value="available">Disponible</SelectItem>
                <SelectItem value="limited">Limité</SelectItem>
                <SelectItem value="unavailable">Indisponible</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {filteredConsultants.length} consultant{filteredConsultants.length > 1 ? 's' : ''} trouvé{filteredConsultants.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Consultants grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredConsultants.map((consultant) => (
            <Card key={consultant.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {consultant.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{consultant.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{consultant.specialty}</p>
                    <div className="mt-2">
                      {getAvailabilityBadge(consultant.availability)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleFavorite(consultant.id)}
                    className={cn(
                      "transition-colors",
                      isFavorite(consultant.id) ? "text-red-500 hover:text-red-600" : "text-muted-foreground hover:text-red-500"
                    )}
                  >
                    <Heart className={cn("h-4 w-4", isFavorite(consultant.id) && "fill-current")} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {consultant.expertise && consultant.expertise.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {consultant.expertise.map((exp) => (
                      <Badge key={exp} variant="outline" className="text-xs">
                        <Award className="h-3 w-3 mr-1" />
                        {exp}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="space-y-2 text-sm">
                  {consultant.hourly_rate && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      <span>{consultant.hourly_rate} {consultant.currency}/h</span>
                    </div>
                  )}
                  {consultant.location && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{consultant.location}</span>
                    </div>
                  )}
                  {consultant.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>{consultant.email}</span>
                    </div>
                  )}
                  {consultant.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{consultant.phone}</span>
                    </div>
                  )}
                </div>
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{consultant.projects_completed}</span> projets complétés
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1" size="sm">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Contacter
                  </Button>
                  <Button variant="outline" size="sm">Profil</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredConsultants.length === 0 && !loading && (
          <Card className="p-12 text-center">
            <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Aucun consultant trouvé</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || specialtyFilter !== 'All' || availabilityFilter !== 'All'
                ? 'Essayez de modifier vos filtres de recherche'
                : 'Aucun consultant disponible pour le moment'}
            </p>
            <CreateConsultantDialog onSuccess={fetchConsultants} />
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Consultants;
